import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  IconButton,
  Typography
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Tune as TuneIcon
} from '@mui/icons-material';
import api from '../services/api';
import { colors } from '../styles';
import { CATEGORY_ICONS } from '../utils/categoryIcons';

const formatCurrency = (value) => Number(value || 0).toLocaleString(undefined, {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const getPaceTone = (category) => {
  const delta = Number(category?.percentConsumed || 0) - Number(category?.percentPeriodElapsed || 0);
  if (Math.abs(delta) <= 5) return 'near';
  return delta > 0 ? 'over' : 'under';
};

const getToneDetails = (tone) => {
  if (tone === 'over') return { color: colors.status.error, label: 'Over pace' };
  if (tone === 'near') return { color: colors.status.syncing, label: 'Near pace' };
  return { color: colors.status.ready, label: 'Under pace' };
};

const AllowanceCard = ({ refreshKey = 0, onConfigure, onOpenDetail }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  // #1114 — optional burndown drill-down (pace series from GET /budgets/current/burndown)
  const [burndown, setBurndown] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [allowanceRes, burndownRes] = await Promise.all([
        api.get('/budgets/daily-allowance'),
        api.get('/budgets/current/burndown').catch(() => null),
      ]);
      setSummary(allowanceRes.data?.data || {
        totalDailyAllowance: 0,
        daysLeft: 0,
        periodProgress: 0,
        categories: []
      });
      setBurndown(burndownRes?.data?.data?.burndown || null);
    } catch (requestError) {
      const message = requestError?.response?.data?.error;
      setError(typeof message === 'string' ? message : 'Daily allowance is unavailable.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const categories = useMemo(() => summary?.categories || [], [summary]);
  const overallTone = useMemo(() => {
    const tones = categories.map(getPaceTone);
    if (tones.includes('over')) return 'over';
    if (tones.includes('near')) return 'near';
    return 'under';
  }, [categories]);
  const toneDetails = getToneDetails(overallTone);

  if (loading && !summary) {
    return (
      <Card sx={{ borderRadius: '20px', boxShadow: 'none' }}>
        <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} sx={{ color: colors.primary }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Daily allowance</Typography>
            <Typography variant="body2" color="text.secondary">
              Calculating your category pace
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ borderRadius: '20px', boxShadow: 'none' }}>
        <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Daily allowance unavailable</Typography>
            <Typography variant="body2" color="text.secondary">{error}</Typography>
          </Box>
          <Button startIcon={<RefreshIcon />} onClick={load}>Try again</Button>
        </CardContent>
      </Card>
    );
  }

  if (!categories.length) {
    return (
      <Card sx={{ borderRadius: '20px', boxShadow: 'none' }}>
        <CardContent sx={{ p: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: `${colors.primary}14`,
              borderRadius: '14px',
              color: colors.primary,
              display: 'flex',
              height: 48,
              justifyContent: 'center',
              width: 48
            }}
          >
            <WalletIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Set your daily spending pace</Typography>
            <Typography variant="body2" color="text.secondary">
              Choose flexible categories to turn what remains into a daily amount.
            </Typography>
          </Box>
          {onConfigure ? (
            <Button variant="contained" startIcon={<TuneIcon />} onClick={onConfigure}>
              Choose categories
            </Button>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: '20px', boxShadow: 'none' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: `${colors.primary}14`,
              borderRadius: '14px',
              color: colors.primary,
              display: 'flex',
              height: 48,
              justifyContent: 'center',
              width: 48
            }}
          >
            <WalletIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Daily allowance</Typography>
            <Typography variant="body2" color="text.secondary">
              Across {categories.length} spending {categories.length === 1 ? 'category' : 'categories'}
            </Typography>
          </Box>
          <IconButton
            aria-label={expanded ? 'Collapse daily allowance details' : 'Expand daily allowance details'}
            aria-expanded={expanded}
            onClick={() => setExpanded(value => !value)}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Box sx={{ alignItems: { xs: 'flex-start', sm: 'center' }, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'space-between', mt: 2 }}>
          <Box sx={{ alignItems: 'baseline', display: 'flex', gap: 0.75 }}>
            <Typography variant="h3" sx={{ color: colors.secondary, fontWeight: 700 }}>
              {formatCurrency(summary?.totalDailyAllowance)}
            </Typography>
            <Typography variant="body1" color="text.secondary">/ day</Typography>
          </Box>
          <Box sx={{ alignItems: 'center', backgroundColor: `${toneDetails.color}14`, borderRadius: '999px', display: 'flex', gap: 1, px: 1.5, py: 0.75 }}>
            <Box sx={{ backgroundColor: toneDetails.color, borderRadius: '50%', height: 8, width: 8 }} />
            <Typography variant="body2" sx={{ color: toneDetails.color, fontWeight: 700 }}>
              {toneDetails.label}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {summary?.daysLeft || 0} {summary?.daysLeft === 1 ? 'day' : 'days'} left in the next period to reset
        </Typography>

        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            {/* #1114 — category remaining + per-day (already in summary); show pace strip when burndown present */}
            {burndown?.days?.length ? (
              <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: 'rgba(20,20,59,0.03)' }} data-testid="web-allowance-pace-strip">
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Daily pace (allowance categories)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 72, overflowX: 'auto' }}>
                  {burndown.days.map((day) => {
                    const spent = Number(day.daySpent || 0);
                    const pace = Number(day.dayPace || burndown.dailyPace || 1);
                    const max = Math.max(pace, spent, 1);
                    const h = Math.max(4, (spent / max) * 64);
                    const over = spent > pace;
                    return (
                      <Box
                        key={day.dateKey || day.d}
                        title={`${day.dateKey || `Day ${day.d}`}: $${spent.toFixed(2)}`}
                        sx={{
                          width: 10,
                          height: h,
                          borderRadius: 1,
                          bgcolor: over ? colors.status.error : colors.primary,
                          flexShrink: 0,
                        }}
                      />
                    );
                  })}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Bars = day spend · dashed pace ≈ ${Number(burndown.dailyPace || 0).toFixed(2)}/day
                </Typography>
              </Box>
            ) : null}
            {categories.map((category) => {
              const details = getToneDetails(getPaceTone(category));
              const consumed = Math.max(0, Math.min(100, Number(category.percentConsumed || 0)));
              const elapsed = Math.max(0, Math.min(100, Number(category.percentPeriodElapsed || 0)));
              const icon = CATEGORY_ICONS[category.icon] || CATEGORY_ICONS.other;

              return (
                <Box key={`${category.budgetId}:${category.categoryId}`} sx={{ borderTop: '1px solid', borderColor: 'divider', py: 2 }}>
                  <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
                    <Box
                      sx={{
                        '& svg': { fontSize: 20 },
                        alignItems: 'center',
                        backgroundColor: `${category.color || colors.primary}14`,
                        borderRadius: '10px',
                        color: category.color || colors.primary,
                        display: 'flex',
                        height: 38,
                        justifyContent: 'center',
                        width: 38
                      }}
                    >
                      {icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body1" noWrap sx={{ fontWeight: 700 }}>
                        {category.categoryName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {category.budgetName} • {formatCurrency(category.remaining)} left • {details.label}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {formatCurrency(category.dailyAllowance)}/day
                    </Typography>
                  </Box>
                  <Box
                    aria-label={`${category.categoryName} spending pace`}
                    aria-valuemax={100}
                    aria-valuemin={0}
                    aria-valuenow={consumed}
                    role="progressbar"
                    sx={{ backgroundColor: '#E9E9EF', borderRadius: '999px', height: 8, mt: 1.5, overflow: 'visible', position: 'relative' }}
                  >
                    <Box sx={{ backgroundColor: details.color, borderRadius: '999px', height: '100%', maxWidth: '100%', width: `${consumed}%` }} />
                    <Box
                      sx={{
                        backgroundColor: colors.secondary,
                        borderRadius: '2px',
                        height: 14,
                        left: `${elapsed}%`,
                        position: 'absolute',
                        top: -3,
                        transform: `translateX(${elapsed === 100 ? -2 : elapsed === 0 ? 0 : -1}px)`,
                        width: 2
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
            <Typography variant="caption" color="text.secondary">
              Fill is spending. Marker is time elapsed.
            </Typography>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default AllowanceCard;
